import { tenantApi, Tenant as TenantType } from '@/api/tenants';
import {
    BuildingStorefrontIcon,
    CheckCircleIcon,
    MagnifyingGlassIcon,
    PlusIcon,
    XCircleIcon,
} from '@heroicons/react/24/outline';
import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const TenantsContainer = styled.div`
  min-height: 100vh;
  background: var(--gray-50);
`;

const TenantsContent = styled.div`
  padding: 2.5rem 0;
`;

const Header = styled.header`
  max-width: 1560px;
  margin: 0 auto;
  padding: 0 2rem;
  margin-bottom: 2rem;
`;

const HeaderTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const Title = styled.h1`
  font-size: 1.875rem;
  font-weight: 700;
  color: var(--gray-900);
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1.25rem;
  background: var(--primary-600);
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }

  &:hover {
    background: var(--primary-700);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(234, 88, 12, 0.3);
  }
`;

const SearchBox = styled.div`
  position: relative;
  width: 300px;

  svg {
    position: absolute;
    left: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    width: 1.25rem;
    height: 1.25rem;
    color: var(--gray-400);
  }

  input {
    width: 100%;
    padding: 0.5rem 1rem 0.5rem 2.5rem;
    border: 1px solid var(--gray-300);
    border-radius: 0.5rem;
    font-size: 0.875rem;

    &:focus {
      outline: none;
      border-color: var(--primary-600);
      box-shadow: 0 0 0 3px rgba(234, 88, 12, 0.1);
    }
  }
`;

const Main = styled.main`
  max-width: 1560px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.25rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: white;
  border-radius: 0.5rem;
  padding: 1.5rem;
  box-shadow: var(--shadow-sm);

  h3 {
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--gray-600);
    margin-bottom: 0.5rem;
  }

  p {
    font-size: 1.875rem;
    font-weight: 700;
    color: var(--gray-900);
  }
`;

const TableWrapper = styled.div`
  background: white;
  border-radius: 0.5rem;
  box-shadow: var(--shadow-sm);
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;

  thead tr {
    background: var(--gray-50);
    border-bottom: 1px solid var(--gray-200);
  }

  thead th {
    padding: 0.75rem 1rem;
    text-align: left;
    font-weight: 600;
    color: var(--gray-600);
    white-space: nowrap;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  tbody tr {
    border-bottom: 1px solid var(--gray-100);
    transition: background 0.15s;

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background: var(--gray-50);
    }
  }

  tbody td {
    padding: 0.875rem 1rem;
    color: var(--gray-800);
    vertical-align: middle;
  }
`;

const TenantName = styled.div`
  font-weight: 600;
  color: var(--gray-900);
`;

const TenantSubdomain = styled.div`
  font-size: 0.75rem;
  color: var(--gray-500);
  font-family: monospace;
  margin-top: 0.125rem;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.375rem;
`;

const ActionBtn = styled.button<{ $variant?: 'danger' | 'success' }>`
  padding: 0.375rem 0.75rem;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid;
  transition: all 0.15s;
  white-space: nowrap;
  border-color: ${(p) =>
    p.$variant === 'danger'
      ? 'var(--error-300)'
      : p.$variant === 'success'
        ? '#6ee7b7'
        : 'var(--gray-300)'};
  background: ${(p) =>
    p.$variant === 'danger'
      ? '#fff1f2'
      : p.$variant === 'success'
        ? '#ecfdf5'
        : 'white'};
  color: ${(p) =>
    p.$variant === 'danger'
      ? 'var(--error-700)'
      : p.$variant === 'success'
        ? '#065f46'
        : 'var(--gray-700)'};

  &:hover {
    filter: brightness(0.95);
  }
`;

const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-top: 1px solid var(--gray-100);
  background: white;
  font-size: 0.875rem;
  color: var(--gray-600);
`;

const PageButtons = styled.div`
  display: flex;
  gap: 0.25rem;
`;

const PageBtn = styled.button<{ $active?: boolean }>`
  min-width: 2rem;
  height: 2rem;
  padding: 0 0.5rem;
  border: 1px solid ${(p) => (p.$active ? 'var(--primary-600)' : 'var(--gray-300)')};
  background: ${(p) => (p.$active ? 'var(--primary-600)' : 'white')};
  color: ${(p) => (p.$active ? 'white' : 'var(--gray-700)')};
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: ${(p) => (p.$active ? '600' : '400')};
  cursor: pointer;
  transition: all 0.15s;

  &:hover:not(:disabled) {
    border-color: var(--primary-600);
    color: ${(p) => (p.$active ? 'white' : 'var(--primary-600)')};
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

const StatusBadge = styled.span<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${(props) => (props.$active ? '#d1fae5' : '#fee2e2')};
  color: ${(props) => (props.$active ? '#065f46' : '#991b1b')};

  svg {
    width: 1rem;
    height: 1rem;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  background: white;
  border-radius: 0.5rem;

  h3 {
    font-size: 1.125rem;
    font-weight: 600;
    color: var(--gray-900);
    margin-bottom: 0.5rem;
  }

  p {
    color: var(--gray-500);
    font-size: 0.875rem;
    margin-bottom: 1.5rem;
  }
`;

const PAGE_SIZE = 20;

const Tenants: FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [tenants, setTenants] = useState<TenantType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const loadTenants = async (p: number, search: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await tenantApi.getTenants({
        page: p,
        per_page: PAGE_SIZE,
        search: search || undefined,
      });
      setTenants(response.tenants || []);
      setTotalPages(response.pages || 1);
      setTotal(response.total ?? response.tenants?.length ?? 0);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load tenants');
      setTenants([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPage(1);
    loadTenants(1, searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    loadTenants(page, searchTerm);
  }, [page]);

  const handleSuspendActivate = async (tenant: TenantType) => {
    try {
      if (tenant.subscription_status === 'active') {
        await tenantApi.suspendTenant(tenant.id, 'Suspended by admin');
      } else {
        await tenantApi.activateTenant(tenant.id);
      }
      loadTenants(page, searchTerm);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to update tenant status');
    }
  };

  const stats = {
    total,
    active: tenants.filter((t) => t.subscription_status === 'active').length,
    suspended: tenants.filter((t) => t.subscription_status === 'suspended').length,
    trial: tenants.filter((t) => t.subscription_status === 'trial').length,
  };

  const pageNumbers = () => {
    const pages: number[] = [];
    const delta = 2;
    const left = Math.max(1, page - delta);
    const right = Math.min(totalPages, page + delta);
    for (let i = left; i <= right; i++) pages.push(i);
    return pages;
  };

  if (error) {
    return (
      <TenantsContainer>
        <TenantsContent>
          <Header>
            <HeaderTop>
              <Title>Tenant Management</Title>
            </HeaderTop>
          </Header>
          <Main>
            <div
              style={{
                padding: '2rem',
                background: 'var(--error-50)',
                border: '1px solid var(--error-200)',
                borderRadius: '0.5rem',
                color: 'var(--error-700)',
              }}
            >
              {error}
            </div>
          </Main>
        </TenantsContent>
      </TenantsContainer>
    );
  }

  return (
    <TenantsContainer>
      <TenantsContent>
        <Header>
          <HeaderTop>
            <Title>Tenant Management</Title>
            <HeaderActions>
              <SearchBox>
                <MagnifyingGlassIcon />
                <input
                  type="text"
                  placeholder="Search tenants..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </SearchBox>
              <AddButton onClick={() => navigate('/tenants/new')}>
                <PlusIcon />
                Add Tenant
              </AddButton>
            </HeaderActions>
          </HeaderTop>
        </Header>

        <Main>
          <StatsGrid>
            <StatCard>
              <h3>Total Tenants</h3>
              <p>{stats.total}</p>
            </StatCard>
            <StatCard>
              <h3>Active</h3>
              <p>{stats.active}</p>
            </StatCard>
            <StatCard>
              <h3>Trial</h3>
              <p>{stats.trial}</p>
            </StatCard>
            <StatCard>
              <h3>Suspended</h3>
              <p>{stats.suspended}</p>
            </StatCard>
          </StatsGrid>

          <TableWrapper>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '4rem' }}>
                <div
                  style={{
                    width: '2rem',
                    height: '2rem',
                    border: '3px solid var(--gray-200)',
                    borderTopColor: 'var(--primary-600)',
                    borderRadius: '50%',
                    animation: 'spin 0.8s linear infinite',
                    margin: '0 auto',
                  }}
                />
              </div>
            ) : tenants.length === 0 ? (
              <EmptyState>
                <BuildingStorefrontIcon
                  style={{
                    width: '3rem',
                    height: '3rem',
                    margin: '0 auto 1rem',
                    color: 'var(--gray-400)',
                  }}
                />
                <h3>No tenants found</h3>
                <p>Try adjusting your search or add your first tenant</p>
                <AddButton onClick={() => navigate('/tenants/new')}>
                  <PlusIcon />
                  Add Tenant
                </AddButton>
              </EmptyState>
            ) : (
              <>
                <Table>
                  <thead>
                    <tr>
                      <th>Company</th>
                      <th>Subdomain</th>
                      <th>Status</th>
                      <th>Tier</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tenants.map((tenant) => (
                      <tr key={tenant.id}>
                        <td>
                          <TenantName>{tenant.company_name}</TenantName>
                        </td>
                        <td>
                          <TenantSubdomain>
                            {tenant.subdomain}.marketeer.com
                          </TenantSubdomain>
                        </td>
                        <td>
                          <StatusBadge
                            $active={tenant.subscription_status === 'active'}
                          >
                            {tenant.subscription_status === 'active' ? (
                              <CheckCircleIcon />
                            ) : (
                              <XCircleIcon />
                            )}
                            {tenant.subscription_status.charAt(0).toUpperCase() +
                              tenant.subscription_status.slice(1)}
                          </StatusBadge>
                        </td>
                        <td style={{ textTransform: 'capitalize' }}>
                          {tenant.subscription_tier || 'starter'}
                        </td>
                        <td>
                          {new Date(tenant.created_at).toLocaleDateString()}
                        </td>
                        <td>
                          <ActionButtons>
                            <ActionBtn
                              onClick={() => navigate(`/tenants/${tenant.id}`)}
                            >
                              View
                            </ActionBtn>
                            <ActionBtn
                              onClick={() =>
                                navigate(`/tenants/edit/${tenant.id}`)
                              }
                            >
                              Edit
                            </ActionBtn>
                            <ActionBtn
                              $variant={
                                tenant.subscription_status === 'active'
                                  ? 'danger'
                                  : 'success'
                              }
                              onClick={() => handleSuspendActivate(tenant)}
                            >
                              {tenant.subscription_status === 'active'
                                ? 'Suspend'
                                : 'Activate'}
                            </ActionBtn>
                          </ActionButtons>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>

                {totalPages > 1 && (
                  <Pagination>
                    <span>
                      Page {page} of {totalPages} &mdash; {total} tenants
                    </span>
                    <PageButtons>
                      <PageBtn
                        onClick={() => setPage(1)}
                        disabled={page === 1}
                      >
                        «
                      </PageBtn>
                      <PageBtn
                        onClick={() => setPage((p) => p - 1)}
                        disabled={page === 1}
                      >
                        ‹
                      </PageBtn>
                      {pageNumbers().map((n) => (
                        <PageBtn
                          key={n}
                          $active={n === page}
                          onClick={() => setPage(n)}
                        >
                          {n}
                        </PageBtn>
                      ))}
                      <PageBtn
                        onClick={() => setPage((p) => p + 1)}
                        disabled={page === totalPages}
                      >
                        ›
                      </PageBtn>
                      <PageBtn
                        onClick={() => setPage(totalPages)}
                        disabled={page === totalPages}
                      >
                        »
                      </PageBtn>
                    </PageButtons>
                  </Pagination>
                )}
              </>
            )}
          </TableWrapper>
        </Main>
      </TenantsContent>
    </TenantsContainer>
  );
};

export default Tenants;
